import React from 'react';
import { Panel } from '../../../../components/containers/Panel';
import { PanelHeader } from '../../../../components/containers/Panel/PanelHeader.view';
import { Toggle } from '../../../../components/forms/Toggle';
import { Table } from '../../../../components/containers/Table';
import { Tbody } from '../../../../components/containers/Table/Tbody';
import { Tr } from '../../../../components/containers/Table/Tr';
import { Td } from '../../../../components/containers/Table/Td';
import { Button } from '../../../../components/actions/Button';
import { ButtonColorsEnum } from '../../../../constants/button-colors';
import { useForm } from 'react-hook-form';
import { FormOptions } from '../../../../constants/form';
import { ToggleAction } from '../../../../components/forms/Toggle/Toggle.controller';
import { UTMSettingsType } from '../LinkTracking.controller';
import { removeTokenCharsFromUTMField } from '../../../../helpers/placeholderHelpers';
import { AdvancedFeatures } from 'components/containers/AdvancedFeature/AdvancedFeature.view';
import { useOrganizationPlan } from 'hooks/useOrganizationPlan';
import { PlanAnalyticsType } from 'models/graphql';
import { AdvancedFeature } from 'components/containers/AdvancedFeature';

export type GoogleAnalyticsPanelViewProps = {
  utmSettings: UTMSettingsType;
  setShowUtmEditModal: (show: boolean) => void;
  removeUTMParametersToggleHandler: ToggleAction;
  customUtmSettingsToggleHandler: ToggleAction;
};

export const GoogleAnalyticsPanelView = (props: GoogleAnalyticsPanelViewProps): JSX.Element => {
  const useFormInstance = useForm({
    ...FormOptions
  });
  const { analyticsType } = useOrganizationPlan();
  const isDisabledFeature = analyticsType === PlanAnalyticsType.BASIC_ANALYTICS;

  return (
    <Panel>
      <div>
        <PanelHeader
          title={'Google Analytics'}
          disabled={isDisabledFeature}
          subTitle={
            !isDisabledFeature
              ? 'Each time you add a new link to a Post, these default settings will be used. You can always override these link settings when editing a Post.'
              : undefined
          }
          titleBadge={<AdvancedFeature feature={AdvancedFeatures.google} />}
        />
      </div>
      <div className={'cell small-6 margin-bottom-small-16'}>
        <Toggle
          disabled={isDisabledFeature}
          formInstance={useFormInstance}
          name={'googleAnalytics'}
          rules={{}}
          label={'Remove Existing UTM Parameters'}
          helper={
            'If you add a link that already contains Google Analytics campaign tracking parameters (UTMs), they can be removed from the link automatically. This helps ensure your own UTM parameters get added, and also lets you hide where you got the link from.'
          }
          action={(value, complete) => props.removeUTMParametersToggleHandler(value, complete)}
          checked={props.utmSettings.shouldRemoveUtm}
        />
      </div>
      <div className={'cell small-6 margin-bottom-small-32'}>
        <Toggle
          disabled={isDisabledFeature}
          formInstance={useFormInstance}
          name={'googleUTM'}
          rules={{}}
          label={'Campaign Tracking (Google Analytics UTMs)'}
          helper={'Add UTM tracking parameters to new links that you share.'}
          action={(value, complete) => props.customUtmSettingsToggleHandler(value, complete)}
          checked={props.utmSettings.campaignTracking}
        />
      </div>
      {props.utmSettings.campaignTracking && (
        <div className={'cell small-6 margin-bottom-small-32'}>
          <Table tableStyle={'stacked_scrolling'}>
            <Tbody>
              <Tr>
                <Td style={['shaded', 'one-line']}>Campaign Source</Td>
                <td>{removeTokenCharsFromUTMField(props.utmSettings.utmSource)}</td>
              </Tr>
              <Tr>
                <Td style={['shaded', 'one-line']}>Campaign Medium</Td>
                <td>{removeTokenCharsFromUTMField(props.utmSettings.utmMedium)}</td>
              </Tr>
              <Tr>
                <Td style={['shaded', 'one-line']}>Campaign Name</Td>
                <td>{removeTokenCharsFromUTMField(props.utmSettings.utmCampaign)}</td>
              </Tr>
              <Tr>
                <Td style={['shaded', 'one-line']}>Campaign Term</Td>
                <td>{removeTokenCharsFromUTMField(props.utmSettings.utmTerm)}</td>
              </Tr>
              <Tr>
                <Td style={['shaded', 'one-line']}>Campaign Content</Td>
                <td>{removeTokenCharsFromUTMField(props.utmSettings.utmContent)}</td>
              </Tr>
            </Tbody>
          </Table>
          <Button
            type={'button'}
            variant={'solid'}
            size={'large'}
            color={ButtonColorsEnum.primary}
            action={() => props.setShowUtmEditModal(true)}>
            Edit Default UTM Parameters
          </Button>
        </div>
      )}
    </Panel>
  );
};
